<!-- Account sign-in, with the legacy invite form for older instances. -->
<script lang="ts">
  import { untrack } from 'svelte';
  import Button from '$lib/ui/Button.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import Logo from '$lib/ui/Logo.svelte';
  import { gate, submitCode, submitAccount } from '$lib/ui/session.svelte';
  import { roomInvitation } from '$lib/social/invite-handoff';
  import { normaliseHandle } from '$lib/social/protocol';

  let { embedded = false, initialMode }: { embedded?: boolean; initialMode?: 'login' | 'register' } = $props();
  let code = $state('');
  let mode = $state<'login' | 'register'>(untrack(() => initialMode ?? (gate.inviteCode && gate.registration === 'invite' ? 'register' : 'login')));
  let email = $state('');
  let username = $state('');
  let password = $state('');
  let inviteCode = $state(gate.inviteCode);
  let deviceLabel = $state('');

  function switchMode(): void {
    mode = mode === 'login' ? 'register' : 'login';
    password = '';
    gate.error = '';
  }

  async function authenticate(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (gate.busy) return;
    gate.error = '';
    if (new TextEncoder().encode(password).length > 256) {
      gate.error = 'Password is too long. Use at most 256 bytes.';
      return;
    }
    if (mode === 'register' && Array.from(password).length < 12) {
      gate.error = 'Use a password with at least 12 characters.';
      return;
    }
    if (mode === 'register' && (normaliseHandle(username) === null || normaliseHandle(username) !== username)) {
      gate.error = 'Use 3 to 24 lowercase letters, numbers and single hyphens for your username.';
      return;
    }
    await submitAccount(mode, {
      email: email.trim(), username, password,
      inviteCode: inviteCode.trim(), deviceLabel: deviceLabel.trim() || 'This browser'
    });
  }
  let field = $state<HTMLInputElement | null>(null);

  $effect(() => {
    field?.focus();
  });

  async function send(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    await submitCode(code);
  }
</script>

<svelte:head><title>Jovii</title></svelte:head>

<section class="gate" class:embedded>
  <div class="welcome" aria-hidden="true">
    <img src="/browse/collection-space-opera.webp" alt="" onerror={(e) => { e.currentTarget.setAttribute('hidden', ''); }} />
    <div class="welcome-copy"><span>JOVII</span><p>A good film.<br />Your kind of company.</p></div>
  </div>
  <div class="card">
    <!-- The wordmark itself, not the J plus the name in a web font beneath it. -->
    <Logo size={40} wordmark={true} />

    <svelte:element this={embedded ? "h3" : "h1"}>{gate.accounts ? (mode === 'register' ? 'Join Jovii.' : 'Welcome back.') : ($roomInvitation.phase !== 'none' ? 'You’re invited.' : 'Your evening starts here.')}</svelte:element>

    {#if gate.state === 'offline'}
      <p class="say">We couldn’t connect to Jovii. Your invitation is still here. Try again in a moment.</p>
      <Button icon="refresh" onclick={() => location.reload()}>Try again</Button>
    {:else if gate.accounts}
      <p class="say">{mode === 'register' ? 'Create your account with the invitation you were sent.' : 'Sign in with your email and password.'}</p>
      {#if $roomInvitation.phase !== 'none'}
        <p class="help">Your room invitation will be here after you sign in.</p>
      {/if}
      <form onsubmit={authenticate} aria-busy={gate.busy} aria-describedby={gate.error ? 'account-error' : undefined}>
        <label for="account-email">Email</label>
        <input id="account-email" name="email" type="email" autocomplete="email" autocapitalize="none" spellcheck="false" required bind:value={email} readonly={gate.busy} />
        {#if mode === 'register'}
          <label for="account-username">Username</label>
          <input id="account-username" name="username" type="text" autocomplete="username" autocapitalize="none" spellcheck="false" required minlength="3" maxlength="24" pattern={String.raw`[a-z0-9]+(?:\-[a-z0-9]+)*`} aria-describedby="username-help" bind:value={username} readonly={gate.busy} />
          <p class="help" id="username-help">3 to 24 lowercase letters, numbers and single hyphens (not at either end). Your username cannot be changed.</p>
        {/if}
        <label for="account-password">Password</label>
        <input id="account-password" name="password" type="password" autocomplete={mode === 'register' ? 'new-password' : 'current-password'} required aria-describedby={mode === 'register' ? 'password-help' : undefined} bind:value={password} readonly={gate.busy} />
        {#if mode === 'register'}
          <p class="help" id="password-help">At least 12 characters, at most 256 bytes.</p>
          <label for="account-invite">Invitation code</label>
          <input id="account-invite" name="inviteCode" type="text" autocomplete="off" autocapitalize="none" spellcheck="false" required bind:value={inviteCode} readonly={gate.busy} />
        {/if}
        <label for="account-device">Device name <span class="help">(optional)</span></label>
        <input id="account-device" name="deviceLabel" type="text" autocomplete="off" placeholder="e.g. Living room laptop" maxlength="80" bind:value={deviceLabel} readonly={gate.busy} />
        <Button type="submit" variant="primary" block size="lg" loading={gate.busy}>
          {mode === 'register' ? 'Create account' : 'Sign in'}
        </Button>
      </form>
      {#if gate.error}<p class="err" id="account-error" role="alert"><Icon name="alert" size={14} /> {gate.error}</p>{/if}
      {#if gate.registration === 'invite'}
        <Button variant="ghost" disabled={gate.busy} onclick={switchMode}>{mode === 'register' ? 'Already have an account? Sign in' : 'Have an invitation? Create account'}</Button>
      {/if}
    {:else}
      <p class="say">{$roomInvitation.phase !== 'none' ? 'Enter your Jovii access code. We’ll take you straight to your room invitation.' : 'A private place to watch, on your own or together. Enter the access code you were sent.'}</p>

      <form onsubmit={send}>
        <label for="access-code">Access code</label>
        <input
          id="access-code"
          aria-describedby="access-help"
          bind:this={field}
          bind:value={code}
          type="text"
          placeholder="willow-amber-…"
          autocomplete="one-time-code"
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          aria-label="Invite code"
          aria-invalid={gate.error ? 'true' : undefined}
        />
        <Button type="submit" variant="primary" block size="lg" loading={gate.busy} disabled={!code.trim()}>
          Continue
        </Button>
      </form>
      <p class="help" id="access-help">Need a code? Ask the person who invited you.</p>
      <p class="privacy"><Icon name="people" size={14} /> Private by default. You choose who joins.</p>

      {#if gate.error}
        <p class="err" role="alert"><Icon name="alert" size={14} /> {gate.error}</p>
      {/if}
    {/if}
  </div>
</section>

<style>
  .gate.embedded { display: block; padding: 0; min-height: 0; }
  .embedded .welcome { display: none; }
  .embedded .card { width: 100%; gap: 14px; }
  .embedded input { border-radius: 8px; }
  .embedded :global(.logo) { display: none; }
  .gate {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    align-items: center;
    min-height: 100dvh;
    padding: clamp(24px, 5vw, 80px);
    gap: clamp(32px, 7vw, 100px);
  }
  .card {
    display: grid;
    justify-items: start;
    gap: 14px;
    width: min(100%, 400px);
    text-align: left;
  }
  h1, h3 {
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-top: 4px;
  }
  .say {
    color: var(--text-dim);
    font-size: 0.85rem;
    line-height: 1.55;
    text-wrap: pretty;
  }
  form {
    display: grid;
    gap: 9px;
    width: 100%;
    margin-top: 10px;
  }
  input {
    height: 46px;
    padding-inline: 15px;
    border-radius: 99px;
    border: 1px solid var(--line);
    background: var(--surface-1);
    color: var(--text);
    /* 16px, like every input: below it iOS zooms the page on focus, and the
       invite screen is the first thing a phone ever sees of this app. */
    font-size: var(--text-md);
    text-align: left;
    outline: none;
    transition: border-color 160ms var(--ease);
  }
  input:focus {
    border-color: color-mix(in oklab, var(--focus-ring) 60%, var(--line));
    background: var(--surface-2);
  }
  input[aria-invalid='true'] {
    border-color: color-mix(in oklab, var(--bad) 65%, var(--line));
  }
  .err {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--bad);
    font-size: 0.8rem;
  }
  .welcome { position: relative; align-self: stretch; min-height: 560px; border-radius: var(--radius); overflow: hidden; background: var(--surface-2); }
  .welcome img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: .7; }
  .welcome::after { content: ''; position: absolute; inset: 0; background: linear-gradient(transparent 25%, rgb(0 0 0 / .85)); }
  .welcome-copy { position: absolute; z-index: 1; inset: auto 10% 9%; color: white; }
  .welcome-copy span { font-size: var(--text-xs); letter-spacing: .16em; font-weight: var(--weight-semi); }
  .welcome-copy p { font-size: clamp(2rem, 4vw, 3.6rem); font-weight: var(--weight-bold); line-height: 1.08; letter-spacing: var(--tracking-tight); margin-top: var(--space-4); }
  label { font-size: var(--text-sm); font-weight: var(--weight-semi); }
  .help { color: var(--text-dim); font-size: var(--text-xs); }
  .privacy { display: flex; gap: 8px; align-items: center; font-size: var(--text-xs); color: var(--text-dim); padding-top: var(--space-4); margin-top: var(--space-2); border-top: 1px solid var(--line); width: 100%; }
  @media (max-width: 767px) { .gate { display: flex; min-height: 100svh; padding: 32px 24px; } .welcome { display: none; } .card { margin: auto; } }
</style>